// ============================================================
// frontend/src/views/SessionDetailView.tsx
// ============================================================
// Session detail screen — the main work surface during/after a
// session. Includes:
//
//   - Header with client name + package + scheduled time
//   - Status chips (scheduled / completed / cancelled / no-show)
//     — clicking one immediately saves the new status
//   - Editable agenda + notes + material + next agenda fields
//     — all auto-saved 800ms after typing stops (useDebouncedSave)
//   - Recent context panel (last 3 completed sessions) — collapsed
//     by default to avoid distracting the therapist
//   - Tags as chip row + simple add-tag input
//   - Links (label + URL) — add/remove
//   - Reschedule + delete in a "more" menu
//
// WHY AUTO-SAVE for notes?
// Therapists write notes between or right after sessions. Asking
// them to click Save constantly is friction. Auto-save with a clear
// "Saving…" → "Saved" indicator gives them the safety net without
// the friction. The useDebouncedSave hook handles all the timing.
// ============================================================

import { useEffect, useState, useCallback } from 'react';
import {
  ArrowLeft, Trash2, Calendar, ChevronDown, ChevronRight,
  Plus, X, Loader2, Check,
} from 'lucide-react';
import { api } from '../lib/api';
import { useSaving } from '../lib/useSaving';
import { useDebouncedSave } from '../lib/useDebouncedSave';
import {
  formatDate, formatDateTime, toDateTimeLocal, fromDateTimeLocal,
} from '../lib/format';
import { Modal, ModalFooter, Field, StatusPill } from '../components/UI';
import { SaveButton } from '../components/SaveButton';
import { ConsiliumLoader } from '../components/ConsiliumLoader';
import type {
  SessionDetailResponse, Session, SessionStatus, SessionLink,
  UpdateSessionRequest,
} from '@consilium/shared';
import type { ViewState } from '../App';

interface SessionDetailViewProps {
  sessionId: string;
  setView: (v: ViewState) => void;
  showError: (msg: string) => void;
}

const STATUS_OPTIONS: { id: SessionStatus; label: string }[] = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'no-show',   label: 'No-show' },
];

export function SessionDetailView({ sessionId, setView, showError }: SessionDetailViewProps) {
  const [data, setData] = useState<SessionDetailResponse | null>(null);

  // Local editable copies of long-form fields. We keep these separate
  // from `data.session` so typing feels instant (controlled inputs).
  // The debounced save then pushes the value to the server.
  const [agenda, setAgenda] = useState('');
  const [notes, setNotes] = useState('');
  const [material, setMaterial] = useState('');
  const [nextAgenda, setNextAgenda] = useState('');

  // Show/hide recent context panel
  const [contextOpen, setContextOpen] = useState(false);

  // Reschedule modal
  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Tag input
  const [newTag, setNewTag] = useState('');

  // ---- Load on mount / when sessionId changes ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<SessionDetailResponse>(`/sessions/${sessionId}`);
        if (!cancelled) {
          setData(d);
          // Seed the editable fields from the loaded session
          setAgenda(d.session.agenda);
          setNotes(d.session.notes);
          setMaterial(d.session.material);
          setNextAgenda(d.session.nextAgenda);
        }
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load session');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ---- Auto-save functions ----
  // Each long-form field has its own debounced save. We use the same
  // session PUT endpoint with a single field at a time. The COALESCE
  // logic on the backend means undefined fields are left unchanged.
  //
  // Wrapped in useCallback so the function identity is stable across
  // renders — otherwise useDebouncedSave would see "a new function"
  // each render and might trigger weirdly.
  const saveAgenda = useCallback(async (v: string) => {
    await api.put(`/sessions/${sessionId}`, { agenda: v } satisfies UpdateSessionRequest);
  }, [sessionId]);
  const saveNotes = useCallback(async (v: string) => {
    await api.put(`/sessions/${sessionId}`, { notes: v } satisfies UpdateSessionRequest);
  }, [sessionId]);
  const saveMaterial = useCallback(async (v: string) => {
    await api.put(`/sessions/${sessionId}`, { material: v } satisfies UpdateSessionRequest);
  }, [sessionId]);
  const saveNextAgenda = useCallback(async (v: string) => {
    await api.put(`/sessions/${sessionId}`, { nextAgenda: v } satisfies UpdateSessionRequest);
  }, [sessionId]);

  const agendaState = useDebouncedSave(agenda, saveAgenda);
  const notesState = useDebouncedSave(notes, saveNotes);
  const materialState = useDebouncedSave(material, saveMaterial);
  const nextAgendaState = useDebouncedSave(nextAgenda, saveNextAgenda);

  // ---- Status change ----
  // This one is NOT auto-debounced because it's a discrete action.
  // We use useSaving so the chip can show a loading state if needed.
  const statusSave = useSaving();
  const setStatus = async (status: SessionStatus) => {
    if (!data || data.session.status === status) return;
    try {
      const result = await statusSave.run(async () => {
        const r = await api.put<{ session: Session }>(`/sessions/${sessionId}`, { status } satisfies UpdateSessionRequest);
        return r.session;
      });
      if (result) setData({ ...data, session: result });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  // ---- Tag operations ----
  const addTag = async () => {
    const t = newTag.trim();
    if (!t || !data) return;
    if (data.session.tags.includes(t)) {
      setNewTag('');
      return;
    }
    const nextTags = [...data.session.tags, t];
    setNewTag('');
    try {
      const r = await api.put<{ session: Session }>(`/sessions/${sessionId}`, { tags: nextTags } satisfies UpdateSessionRequest);
      setData({ ...data, session: r.session });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add tag');
    }
  };

  const removeTag = async (tag: string) => {
    if (!data) return;
    const nextTags = data.session.tags.filter((t) => t !== tag);
    try {
      const r = await api.put<{ session: Session }>(`/sessions/${sessionId}`, { tags: nextTags } satisfies UpdateSessionRequest);
      setData({ ...data, session: r.session });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove tag');
    }
  };

  // ---- Link operations ----
  const addLink = async (label: string, url: string) => {
    if (!data || !label.trim() || !url.trim()) return;
    const newLink: SessionLink = {
      id: crypto.randomUUID(),
      label: label.trim(),
      url: url.trim(),
    };
    const nextLinks = [...data.session.links, newLink];
    try {
      const r = await api.put<{ session: Session }>(`/sessions/${sessionId}`, { links: nextLinks } satisfies UpdateSessionRequest);
      setData({ ...data, session: r.session });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add link');
    }
  };

  const removeLink = async (id: string) => {
    if (!data) return;
    const nextLinks = data.session.links.filter((l) => l.id !== id);
    try {
      const r = await api.put<{ session: Session }>(`/sessions/${sessionId}`, { links: nextLinks } satisfies UpdateSessionRequest);
      setData({ ...data, session: r.session });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove link');
    }
  };

  // ---- Delete ----
  const handleDelete = async () => {
    try {
      await api.del(`/sessions/${sessionId}`);
      // Go back to the client they were viewing.
      if (data) setView({ name: 'client', clientId: data.client.id });
      else setView({ name: 'home' });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  if (!data) return <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><ConsiliumLoader size={48} /></div>;

  const { session, client, package: pkg, recentContext } = data;

  return (
    <div className="fade-in">
      <button
        className="btn-icon"
        onClick={() => setView({ name: 'client', clientId: client.id })}
        style={{ marginBottom: 16, fontSize: 13, padding: '6px 10px', minWidth: 0 }}
      >
        <ArrowLeft size={14} style={{ marginRight: 6 }} />Back to {client.name}
      </button>

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="mono-cap">Session</div>
          <h1 className="page-title">{formatDateTime(session.scheduledAt)}</h1>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
            {client.name} · {pkg.name}
            {pkg.isOneOff && <span className="pill pill-oneoff" style={{ marginLeft: 8 }}>One-off</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn-icon" onClick={() => setShowReschedule(true)} title="Reschedule">
            <Calendar size={15} />
          </button>
          <button className="btn-icon" onClick={() => setConfirmDelete(true)} title="Delete">
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {/* Status chips — click to change */}
      <div style={{ marginTop: 18 }}>
        <div className="label">Status</div>
        <div className="chip-row">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              className={`chip ${session.status === opt.id ? 'active' : ''}`}
              onClick={() => setStatus(opt.id)}
              disabled={statusSave.saving}
            >
              {opt.label}
            </button>
          ))}
          {statusSave.saving && (
            <span className="save-indicator is-saving" style={{ marginLeft: 6 }}>
              <Loader2 size={12} className="spin" />Saving…
            </span>
          )}
        </div>
      </div>

      {/* Recent context — collapsed by default */}
      {recentContext.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button
            onClick={() => setContextOpen(!contextOpen)}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 12, padding: '12px 16px', width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', color: 'var(--ink)', transition: 'all 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {contextOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                Recent context · last {recentContext.length} session{recentContext.length > 1 ? 's' : ''}
              </span>
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {contextOpen ? 'Hide' : 'Show'}
            </span>
          </button>
          {contextOpen && (
            <div className="stack" style={{ marginTop: 8 }}>
              {recentContext.map((s) => (
                <div key={s.id} className="card" style={{ background: 'var(--bg-overlay)' }}>
                  <div style={{ fontSize: 13, color: 'var(--muted-strong)', marginBottom: 6 }}>
                    {formatDate(s.scheduledAt)}
                  </div>
                  {s.agenda && (
                    <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginBottom: 6 }}>
                      <strong>Agenda:</strong> {s.agenda}
                    </div>
                  )}
                  {s.notes && (
                    <div style={{ fontSize: 13, color: 'var(--ink-soft)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                      {s.notes}
                    </div>
                  )}
                  {s.nextAgenda && (
                    <div style={{ fontSize: 13, color: 'var(--accent-soft)', marginTop: 8 }}>
                      <strong>Next:</strong> {s.nextAgenda}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editable fields with auto-save indicators */}
      <div className="stack-lg" style={{ marginTop: 24 }}>
        <FieldWithAutoSave label="Agenda" state={agendaState}>
          <input
            className="input" value={agenda}
            placeholder="What's the plan for this session?"
            onChange={(e) => setAgenda(e.target.value)}
          />
        </FieldWithAutoSave>

        <FieldWithAutoSave label="Notes" state={notesState}>
          <textarea
            className="input" value={notes}
            placeholder="What happened, what was discussed, observations…"
            style={{ minHeight: 200 }}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FieldWithAutoSave>

        <FieldWithAutoSave label="Material / homework given" state={materialState}>
          <textarea
            className="input" value={material}
            placeholder="Worksheets, handouts, between-session exercises…"
            onChange={(e) => setMaterial(e.target.value)}
          />
        </FieldWithAutoSave>

        <FieldWithAutoSave label="Plan for next session" state={nextAgendaState}>
          <textarea
            className="input" value={nextAgenda}
            placeholder="Topics to follow up on, exercises to review…"
            onChange={(e) => setNextAgenda(e.target.value)}
          />
        </FieldWithAutoSave>

        {/* Tags */}
        <div>
          <div className="label">Tags</div>
          <div className="chip-row" style={{ marginBottom: 8 }}>
            {session.tags.map((tag) => (
              <span
                key={tag} className="chip"
                style={{ paddingRight: 8 }}
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  style={{
                    background: 'none', border: 'none', marginLeft: 4,
                    color: 'inherit', cursor: 'pointer', display: 'inline-flex',
                  }}
                  aria-label={`Remove ${tag}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input" value={newTag}
              placeholder="Add a tag (e.g. breakthrough, homework given)…"
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            />
            <button
              className="btn-ghost" onClick={addTag}
              disabled={!newTag.trim()}
              style={{ flexShrink: 0 }}
            >
              <Plus size={14} />Add
            </button>
          </div>
        </div>

        {/* Links */}
        <LinksSection
          links={session.links}
          onAdd={addLink}
          onRemove={removeLink}
        />
      </div>

      {/* Modals */}
      {showReschedule && (
        <RescheduleForm
          session={session}
          onSaved={(s) => { setData({ ...data, session: s }); setShowReschedule(false); }}
          onCancel={() => setShowReschedule(false)}
          showError={showError}
        />
      )}
      {confirmDelete && (
        <Modal title="Delete session?" onClose={() => setConfirmDelete(false)}>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            Permanently delete this session and its notes? This cannot be undone.
          </div>
          <ModalFooter>
            <button className="btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button className="btn-danger" onClick={handleDelete}>Delete</button>
          </ModalFooter>
        </Modal>
      )}

      {/* Bottom padding so the bottom-nav doesn't cover the last field on mobile */}
      <div style={{ height: 40 }} />
    </div>
  );
}

// ============================================================
// FieldWithAutoSave — label + auto-save status indicator + child input
// ============================================================
interface AutoSaveState {
  saving: boolean;
  saved: boolean;
  isDirty: boolean;
  error: Error | null;
}
function FieldWithAutoSave({ label, state, children }: {
  label: string;
  state: AutoSaveState;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <label className="label" style={{ marginBottom: 0 }}>{label}</label>
        <AutoSaveStatus state={state} />
      </div>
      {children}
    </div>
  );
}

function AutoSaveStatus({ state }: { state: AutoSaveState }) {
  if (state.error) {
    return (
      <span className="save-indicator" style={{ color: 'var(--danger)' }}>
        Save failed
      </span>
    );
  }
  if (state.saving) {
    return (
      <span className="save-indicator is-saving">
        <Loader2 size={11} className="spin" />Saving…
      </span>
    );
  }
  if (state.saved) {
    return (
      <span className="save-indicator is-saved">
        <Check size={11} />Saved
      </span>
    );
  }
  if (state.isDirty) {
    return <span className="save-indicator">Unsaved</span>;
  }
  return null;
}

// ============================================================
// LinksSection — inline links list with add/remove
// ============================================================
function LinksSection({ links, onAdd, onRemove }: {
  links: SessionLink[];
  onAdd: (label: string, url: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!label.trim() || !url.trim() || adding) return;
    setAdding(true);
    try {
      await onAdd(label, url);
      setLabel('');
      setUrl('');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <div className="label">Links</div>
      {links.length > 0 && (
        <div className="stack" style={{ gap: 6, marginBottom: 10 }}>
          {links.map((l) => (
            <div key={l.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', background: 'var(--bg-elevated)',
              border: '1px solid var(--border)', borderRadius: 10,
            }}>
              <a
                href={l.url} target="_blank" rel="noopener noreferrer"
                style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {l.label}
              </a>
              <button
                className="btn-icon" onClick={() => onRemove(l.id)}
                style={{ minWidth: 32, minHeight: 32, padding: 6 }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="grid-2-1" style={{ gap: 8 }}>
        <input
          className="input" value={label}
          placeholder="Label (e.g. Self-compassion meditation)"
          onChange={(e) => setLabel(e.target.value)}
        />
        <input
          className="input" value={url}
          placeholder="https://…"
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <button
          className="btn-ghost" onClick={handleAdd}
          disabled={!label.trim() || !url.trim() || adding}
        >
          <Plus size={14} />Add link
        </button>
      </div>
    </div>
  );
}

// ============================================================
// RescheduleForm
// ============================================================
function RescheduleForm({ session, onSaved, onCancel, showError }: {
  session: Session;
  onSaved: (s: Session) => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}) {
  const [scheduledAt, setScheduledAt] = useState(toDateTimeLocal(session.scheduledAt));
  const [durationMin, setDurationMin] = useState(session.durationMin);
  const { saving, saved, run } = useSaving();

  const onSubmit = async () => {
    if (!scheduledAt) return;
    try {
      const result = await run(async () => {
        const r = await api.put<{ session: Session }>(`/sessions/${session.id}`, {
          scheduledAt: fromDateTimeLocal(scheduledAt),
          durationMin,
        } satisfies UpdateSessionRequest);
        return r.session;
      });
      if (result) setTimeout(() => onSaved(result), 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reschedule');
    }
  };

  return (
    <Modal title="Reschedule session" onClose={onCancel}>
      <div className="stack-lg">
        <Field label="When" required>
          <input
            type="datetime-local" className="input"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </Field>
        <Field label="Duration (min)">
          <input
            type="number" min={15} step={5} className="input"
            value={durationMin}
            onChange={(e) => setDurationMin(Number(e.target.value))}
          />
        </Field>
      </div>
      <ModalFooter>
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <SaveButton
          saving={saving} saved={saved} onClick={onSubmit}
          disabled={!scheduledAt}
        >
          Save changes
        </SaveButton>
      </ModalFooter>
    </Modal>
  );
}
